import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useLoading } from '../contexts/LoadingContext';
import './AnimatedBackground.css';

function calcSpread(camera: THREE.PerspectiveCamera, depth: number, scalar: number) {
    // Calculate spread based on camera viewport at depth/2
    // This makes the spread proportional to the visible area
    const fov = camera.fov * (Math.PI / 180); // Convert to radians
    const viewportHeight = 2 * Math.tan(fov / 2) * (depth / 2);
    const viewportWidth = viewportHeight * camera.aspect;
    const spread = Math.max(viewportWidth, viewportHeight) * scalar; // % of viewport dimension
    return spread;
}

const NORMAL_SPEED = 0.3;
const LOADING_SPEED = 10.0;
const NORMAL_SIZE = 1.0;
const LOADING_SIZE = 1.8;
const NORMAL_TRAIL_ALPHA = 1.0;
const LOADING_TRAIL_ALPHA = 0.01;
const LERP_RATE_IN = 0.03;
const LERP_RATE_OUT = 0.03;

function lerp(current: number, target: number, rate: number): number {
  return current + (target - current) * rate;
}

const AnimatedBackground: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const starsRef = useRef<THREE.Points | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isVisibleRef = useRef<boolean>(true);
  const isLoadingRef = useRef<boolean>(false);

  const { isLoading } = useLoading();

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    const sceneScalar = 1;
    const depth = 80 * sceneScalar;   // Z depth

    // Interpolated animation parameters
    let currentSpeed = NORMAL_SPEED * sceneScalar;
    let currentSize = NORMAL_SIZE;
    let currentTrailAlpha = NORMAL_TRAIL_ALPHA;

    // Camera setup - looking down negative Z axis (default)
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      depth
    );
    camera.position.set(0, 0, 0);
    cameraRef.current = camera;

    const spread = calcSpread(camera, depth, 1);


    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: false });
    renderer.setSize(window.innerWidth, window.innerHeight);
    // Cap pixel ratio to reduce rendering load
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.autoClear = false;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Fade quad for trail effect
    const fadeGeometry = new THREE.PlaneGeometry(2, 2);
    const fadeMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: NORMAL_TRAIL_ALPHA,
      depthTest: false,
      depthWrite: false,
    });
    const fadeMesh = new THREE.Mesh(fadeGeometry, fadeMaterial);
    const fadeScene = new THREE.Scene();
    fadeScene.add(fadeMesh);
    const fadeCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    // Create starfield
    const starCount = 300;
    const starGeometry = new THREE.BufferGeometry();
    const starPositions = new Float32Array(starCount * 3);


    for (let i = 0; i < starCount; i++) {
      const i3 = i * 3;

      starPositions[i3] = (Math.random() - 0.5) * spread;          // x
      starPositions[i3 + 1] = (Math.random() - 0.5) * spread;      // y
      starPositions[i3 + 2] = -Math.random() * depth;              // z (negative, in front of camera)
    }

    starGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));

    // Star material
    const starMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: NORMAL_SIZE,
      sizeAttenuation: false,
    });

    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);
    starsRef.current = stars;


    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      // Skip rendering if tab is not visible
      if (!isVisibleRef.current) return;

      const loading = isLoadingRef.current;
      const lerpRate = loading ? LERP_RATE_IN : LERP_RATE_OUT;

      const targetSpeed = loading ? LOADING_SPEED * sceneScalar : NORMAL_SPEED * sceneScalar;
      const targetSize = loading ? LOADING_SIZE : NORMAL_SIZE;
      const targetTrailAlpha = loading ? LOADING_TRAIL_ALPHA : NORMAL_TRAIL_ALPHA;

      currentSpeed = lerp(currentSpeed, targetSpeed, lerpRate);
      currentSize = lerp(currentSize, targetSize, lerpRate);
      currentTrailAlpha = lerp(currentTrailAlpha, targetTrailAlpha, lerpRate);

      starMaterial.size = currentSize;

      const positions = starGeometry.attributes.position.array as Float32Array;

      // Move stars toward camera and wrap
      for (let i = 0; i < starCount; i++) {
        const i3 = i * 3;

        // Move star toward camera
        positions[i3 + 2] += currentSpeed;

        // If star passes camera, reset to far distance
        if (positions[i3 + 2] > 0) {
          positions[i3] = (Math.random() - 0.5) * spread;
          positions[i3 + 1] = (Math.random() - 0.5) * spread;
          positions[i3 + 2] = -depth;
        }
      }

      starGeometry.attributes.position.needsUpdate = true;
      container.dataset.starSpeed = currentSpeed.toFixed(2);

      if (currentTrailAlpha > 0.99) {
        // No trails — clear and render normally
        renderer.clear();
        renderer.render(scene, camera);
      } else {
        // Trail effect: render fade quad then stars (no clear)
        fadeMaterial.opacity = currentTrailAlpha;
        renderer.render(fadeScene, fadeCamera);
        renderer.render(scene, camera);
      }
    };

    animate();

    // Handle window resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.clear();
    };

    window.addEventListener('resize', handleResize);

    // Handle visibility change to pause when tab is hidden
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      if (rendererRef.current) {
        container.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();
      }

      if (starGeometry) {
        starGeometry.dispose();
      }

      if (starMaterial) {
        starMaterial.dispose();
      }

      fadeGeometry.dispose();
      fadeMaterial.dispose();
    };
  }, []);

  return <div ref={containerRef} className="animated-background" data-loading={isLoading} />;
};

export default AnimatedBackground;
